/**
 * Intent Intelligence — SLICE 4: merge the deterministic suggester and the
 * LLM inference into ONE unioned suggestion set.
 *
 * `mergeIntentSuggestions(deterministic, llm, currentIntent)` produces a
 * `SuggestedFullIntent` — the per-field union of:
 *   - `suggestProjectIntent(...)`  (the deterministic 6-tier suggester), and
 *   - `inferIntentFromContext(...)` (the LLM layer, `source:'llm-inferred'`).
 *
 * MERGE RULES (in order):
 *   1. EXCLUDE any field whose `currentIntent.fieldSource[field]` is
 *      user-authored (`'user'` / `'accepted'` / `'manual'`). USER ALWAYS WINS —
 *      we never offer to overwrite a value the user owns, regardless of which
 *      layer suggested it. (The lower layers already gate on this, but we
 *      re-assert here so the merge is the single, falsifiable choke point.)
 *   2. Where BOTH layers suggest a field, the HIGHER-CONFIDENCE suggestion
 *      wins. Deterministic ties (equal confidence) keep the DETERMINISTIC
 *      value — it's reproducible and oracle-backed, so it's the safer default.
 *   3. Where only one layer suggests a field, that suggestion is taken as-is.
 *   4. Provenance is ALWAYS recorded (the winning `IntentSuggestion.source`),
 *      so the banner / chip UI can attribute every suggested value.
 *
 * Pure + deterministic — no store, no network, no side effects.
 */

import type {
  IntentSuggestion,
  SuggestedProjectIntent,
  WorkloadProfileId,
  NuclearInterest,
  Priority,
  CoolingStrategy,
  BopHeatRejectionId,
  TimelineBucketId,
} from '../suggestProjectIntent';
import type { SuggestedFields } from '../../services/inferIntentFromContext';
import type { ProjectIntent } from '../../types/siteRag';

/** Provenance tiers that mean the USER owns the field — never overwrite. */
const USER_AUTHORED_SOURCES: ReadonlySet<string> = new Set(['user', 'accepted', 'manual']);

/**
 * The full, unioned suggestion set. Every key is optional — a field is present
 * only when at least one (non-excluded) layer produced a suggestion for it.
 * Carbon / tier / sponsor / PUE come only from the LLM layer today; the rest
 * can come from either layer (higher-confidence wins).
 */
export interface SuggestedFullIntent {
  workloadProfile?: IntentSuggestion<WorkloadProfileId>;
  nuclearInterest?: IntentSuggestion<NuclearInterest>;
  priority?: IntentSuggestion<Priority>;
  coolingStrategy?: IntentSuggestion<CoolingStrategy>;
  bopHeatRejection?: IntentSuggestion<BopHeatRejectionId>;
  timeline?: IntentSuggestion<TimelineBucketId>;
  /** Exact in-service date (deterministic only — the LLM uses the bucket). */
  inServiceDate?: IntentSuggestion<string>;
  powerRequirementMW?: IntentSuggestion<number>;
  pueValue?: IntentSuggestion<number>;
  carbonCommitment?: IntentSuggestion<string>;
  // BTM DECIDE-NEXT decision fields (#34) — deterministic suggester now infers
  // these from reports/docs/site/intent, so the embedded form's chips can light
  // up off live intelligence (not just the DECIDE-NEXT inline path). Held as
  // string so the banner reads them uniformly; the write boundary maps the
  // string onto the nested ProjectIntent shape (offtake.structure / .target).
  capacityCreditIntent?: IntentSuggestion<string>;
  offtake?: IntentSuggestion<string>;
  reactorDesignClass?: IntentSuggestion<string>;
  datacenterTierTarget?: IntentSuggestion<string>;
  /** Projects onto `ProjectIntent.siteSponsor.type` at the write boundary. */
  siteSponsorType?: IntentSuggestion<string>;
}

/** True when `field` (or any alias) is user-authored on the current intent. */
function isUserAuthored(
  currentIntent: Pick<ProjectIntent, 'fieldSource'> | undefined,
  ...fields: string[]
): boolean {
  const src = currentIntent?.fieldSource;
  if (!src) return false;
  return fields.some((f) => {
    const v = (src as Record<string, string | undefined>)[f];
    return typeof v === 'string' && USER_AUTHORED_SOURCES.has(v);
  });
}

/**
 * Pick the winner between a deterministic and an LLM suggestion for one field.
 * - Both present  → higher confidence wins; tie → deterministic (reproducible).
 * - One present   → that one.
 * - Neither       → undefined.
 */
function pickWinner<T>(
  deterministic: IntentSuggestion<T> | undefined,
  llm: IntentSuggestion<T> | undefined,
): IntentSuggestion<T> | undefined {
  if (deterministic && llm) {
    // Strictly-greater LLM confidence wins; equal keeps deterministic.
    return llm.confidence > deterministic.confidence ? llm : deterministic;
  }
  return deterministic ?? llm;
}

/**
 * Merge the deterministic + LLM suggestion sets into one `SuggestedFullIntent`.
 *
 * @param deterministic  output of `suggestProjectIntent` (may be partial).
 * @param llm            output of `inferIntentFromContext` (may be empty).
 * @param currentIntent  the site's current intent — its `fieldSource`
 *                       provenance gates USER-WINS exclusion.
 */
export function mergeIntentSuggestions(
  deterministic: SuggestedProjectIntent | undefined,
  llm: SuggestedFields | undefined,
  currentIntent?: Pick<ProjectIntent, 'fieldSource'>,
): SuggestedFullIntent {
  const d = deterministic;
  const l = llm ?? {};
  const out: SuggestedFullIntent = {};

  // The deterministic suggester always emits workloadProfile / nuclearInterest
  // / priority (it falls back to a `'fallback'` tier rather than omitting), so
  // we treat the fallback tier as "present but weak" — the union still honours
  // higher-confidence LLM over it, and excludes it if the user owns the field.

  // workloadProfile
  if (!isUserAuthored(currentIntent, 'workloadProfile')) {
    const w = pickWinner<WorkloadProfileId>(d?.workloadProfile, l.workloadProfile);
    if (w) out.workloadProfile = w;
  }

  // nuclearInterest
  if (!isUserAuthored(currentIntent, 'nuclearInterest')) {
    const n = pickWinner<NuclearInterest>(d?.nuclearInterest, l.nuclearInterest);
    if (n) out.nuclearInterest = n;
  }

  // priority
  if (!isUserAuthored(currentIntent, 'priority')) {
    const p = pickWinner<Priority>(d?.priority, l.priority);
    if (p) out.priority = p;
  }

  // coolingStrategy
  if (!isUserAuthored(currentIntent, 'coolingStrategy')) {
    const c = pickWinner<CoolingStrategy>(d?.coolingStrategy, l.coolingStrategy);
    if (c) out.coolingStrategy = c;
  }

  // bopHeatRejection
  if (!isUserAuthored(currentIntent, 'bopHeatRejection')) {
    const b = pickWinner<BopHeatRejectionId>(d?.bopHeatRejection, l.bopHeatRejection);
    if (b) out.bopHeatRejection = b;
  }

  // timeline (bucket) + inServiceDate (exact). Both gate on the timeline /
  // inServiceDate provenance pair (either being user-authored locks both).
  if (!isUserAuthored(currentIntent, 'timeline', 'inServiceDate')) {
    const t = pickWinner<TimelineBucketId>(d?.timeline, l.timeline);
    if (t) out.timeline = t;
    // inServiceDate only comes from the deterministic layer.
    if (d?.inServiceDate) out.inServiceDate = d.inServiceDate;
  }

  // powerRequirementMW (MW + bucket provenance pair)
  if (!isUserAuthored(currentIntent, 'powerRequirementMW', 'powerRequirement')) {
    const mw = pickWinner<number>(d?.powerRequirementMW, l.powerRequirementMW);
    if (mw) out.powerRequirementMW = mw;
  }

  // pueValue — LLM-only today.
  if (!isUserAuthored(currentIntent, 'pueValue') && l.pueValue) {
    out.pueValue = l.pueValue;
  }

  // carbonCommitment — deterministic (#34) + LLM. Higher confidence wins;
  // deterministic emits a non-fallback value only on a real carbon signal.
  if (!isUserAuthored(currentIntent, 'carbonCommitment')) {
    const dCarbon = d?.carbonCommitment && d.carbonCommitment.source !== 'fallback'
      ? (d.carbonCommitment as IntentSuggestion<string>)
      : undefined;
    const c = pickWinner<string>(dCarbon, l.carbonCommitment);
    if (c) out.carbonCommitment = c;
  }

  // capacityCreditIntent / offtake / reactorDesignClass — deterministic-only
  // (#34). Surface only a real (non-fallback) inferred signal; the banner +
  // chip UI filter handles the rest. reactorDesignClass stays dependency-gated
  // upstream (only inferred once nuclearInterest is active).
  if (!isUserAuthored(currentIntent, 'capacityCreditIntent')
      && d?.capacityCreditIntent && d.capacityCreditIntent.source !== 'fallback') {
    out.capacityCreditIntent = d.capacityCreditIntent as IntentSuggestion<string>;
  }
  if (!isUserAuthored(currentIntent, 'offtake')
      && d?.offtake && d.offtake.source !== 'fallback') {
    out.offtake = d.offtake as IntentSuggestion<string>;
  }
  if (!isUserAuthored(currentIntent, 'reactorDesignClass')
      && d?.reactorDesignClass && d.reactorDesignClass.source !== 'fallback') {
    out.reactorDesignClass = d.reactorDesignClass as IntentSuggestion<string>;
  }

  // datacenterTierTarget — LLM-only today.
  if (!isUserAuthored(currentIntent, 'datacenterTierTarget') && l.datacenterTierTarget) {
    out.datacenterTierTarget = l.datacenterTierTarget;
  }

  // siteSponsorType — LLM-only today (projects onto siteSponsor.type).
  if (!isUserAuthored(currentIntent, 'siteSponsorType', 'siteSponsor') && l.siteSponsorType) {
    out.siteSponsorType = l.siteSponsorType;
  }

  return out;
}
